import React, { useState, useRef } from "react";
import { Sparkles } from "lucide-react";

export const CRO_MENU_GROUPS = [
  {
    id: "overview", label: "Overview", persona: "Executive",
    items: [
      { value: "executive",  label: "Executive · L1",       description: "Portfolio KPIs & trends" },
      { value: "portfolio",  label: "Portfolio · L2",       description: "Studies at a glance" },
      { value: "study",      label: "Study Workspace · L3", description: "Deep-dive per trial" },
    ],
  },
  {
    id: "operations", label: "Operations", persona: "Site Ops",
    items: [
      { value: "recruitment",   label: "Recruitment · L4",    description: "Enrolment funnel & velocity" },
      { value: "sites",         label: "Sites · L8",          description: "Site leaderboard & activation" },
      { value: "vendors",       label: "Vendors · L9",        description: "CRO scorecards" },
      { value: "investigators", label: "Investigators · L15", description: "PI directory & rankings" },
    ],
  },
  {
    id: "financial", label: "Financial", persona: "Portfolio Lead",
    items: [
      { value: "financial", label: "Financial · L5", description: "Budget burn & cost per patient" },
    ],
  },
  {
    id: "compliance", label: "Compliance", persona: "Regulatory",
    items: [
      { value: "dataquality", label: "Data Quality · L6", description: "EDC queries & DB-lock readiness" },
      { value: "regulatory",  label: "Regulatory · L7",   description: "CDSCO / DCGI / Ethics filings" },
      { value: "deviations",  label: "Deviations · L11",  description: "Protocol deviations & trends" },
    ],
  },
  {
    id: "safety", label: "Safety", persona: "Safety",
    items: [
      { value: "safety", label: "Safety · L10", description: "AE / SAE deep-dive" },
    ],
  },
  {
    id: "predictive", label: "Predictive", persona: "AI Insights",
    items: [
      { value: "digitaltwin",  label: "Digital Twin · L12",  description: "What-if study simulations" },
      { value: "outcomes",     label: "Outcomes · L13",      description: "Endpoints & subgroups" },
      { value: "patientvoice", label: "Patient Voice · L14", description: "PRO satisfaction & dropout" },
      { value: "predictions",  label: "Predictive AI · L17", description: "Success probability & drivers" },
    ],
  },
  {
    id: "intelligence", label: "Intelligence", persona: "AI Insights",
    items: [
      { value: "rwe",        label: "RWE / Biomarkers · L16", description: "Real-world evidence & enrichment" },
      { value: "graph",      label: "Knowledge Graph · L18",  description: "Entity relationships" },
      { value: "enterprise", label: "Enterprise · L19",       description: "ROI, KPIs, departments" },
      { value: "copilot",    label: "Co-Pilot · L20",         description: "LLM research assistant", accent: true },
    ],
  },
];

export default function CroHeaderMenu({ active, onChange }) {
  const [openGroup, setOpenGroup] = useState(null);
  const closeTimer = useRef(null);
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenGroup(null), 120);
  };
  const openNow = (id) => { cancelClose(); setOpenGroup(id); };
  const activeGroupId = CRO_MENU_GROUPS.find((g) => g.items.some((i) => i.value === active))?.id;
  return (
    <div className="sticky top-16 lg:top-0 z-30 -mx-4 lg:-mx-6 xl:-mx-8 px-4 lg:px-6 xl:px-8 border-b border-border bg-white/95 backdrop-blur-md" data-testid="cro-header-menu">
      <div className="overflow-x-auto">
        <nav className="flex items-stretch gap-1 h-12 w-max min-w-full">
          {CRO_MENU_GROUPS.map((group) => {
            const isActive = activeGroupId === group.id;
            const isOpen = openGroup === group.id;
            return (
              <div key={group.id} className="relative">
                <button
                  type="button"
                  data-testid={`cro-menu-group-${group.id}`}
                  onMouseEnter={() => openNow(group.id)}
                  onMouseLeave={scheduleClose}
                  onFocus={() => openNow(group.id)}
                  onClick={() => (isOpen ? setOpenGroup(null) : openNow(group.id))}
                  className={`h-full px-4 text-xs font-semibold uppercase tracking-wider transition-colors border-b-2 ${
                    isActive
                      ? "text-primary border-primary"
                      : isOpen
                        ? "text-primary border-accent/60 bg-muted/40"
                        : "text-muted-foreground border-transparent hover:text-primary hover:border-accent/40"
                  }`}
                >
                  {group.label}
                  <span className="ml-1.5 text-[10px] font-normal normal-case text-muted-foreground/80">
                    {group.items.length}
                  </span>
                </button>
                {isOpen && (
                  <div
                    className="absolute left-0 top-full mt-0 z-40 min-w-[280px] rounded-b-lg border border-t-0 border-border bg-white shadow-lg overflow-hidden"
                    data-testid={`cro-menu-dropdown-${group.id}`}
                    onMouseEnter={() => openNow(group.id)}
                    onMouseLeave={scheduleClose}
                  >
                    <div className="px-4 py-2 bg-muted/40 border-b border-border">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{group.persona}</div>
                      <div className="text-xs text-primary font-medium">{group.label}</div>
                    </div>
                    <ul className="py-1">
                      {group.items.map((item) => {
                        const isSelected = active === item.value;
                        return (
                          <li key={item.value}>
                            <button
                              type="button"
                              data-testid={`cro-menu-item-${item.value}`}
                              onClick={() => {
                                onChange(item.value);
                                setOpenGroup(null);
                                cancelClose();
                              }}
                              className={`w-full text-left px-4 py-2 transition-colors ${
                                isSelected
                                  ? "bg-pastel-mint-bg text-pastel-mint-fg"
                                  : "hover:bg-muted/60"
                              }`}
                            >
                              <div className={`text-xs font-medium flex items-center gap-1.5 ${item.accent && !isSelected ? "text-accent" : ""}`}>
                                {item.accent && <Sparkles size={11} />}
                                {item.label}
                                {isSelected && <span className="ml-auto text-[10px] uppercase tracking-wider">Active</span>}
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">{item.description}</div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
